import { JobTableName } from '../../../redux/application/application.types';

// Die Spalten-Registry ist der Vertrag zwischen der Mandanten-Config
// (features.fieldVisibility.jobTables) und den beiden Auftragstabellen — und
// die Vorlage für die Config-Oberfläche (#892): je Spalte eine Zeile mit
// Sichtbarkeits-Schalter, kein Freitextfeld für Spalten-Keys.
//
// Bewusst ein EIGENES Vokabular, nicht das des Excel-Exports: gleich aussehende
// Keys stehen dort für Verschiedenes. Der Export liefert unter
// 'meter.address.city' "Bremen / Findorff" in einer Spalte, die Tabelle trennt
// Stadt und Stadtteil in zwei ('meter.meterAddress.city',
// 'meter.meterAddress.cityDistrict'). Ein gemeinsames Vokabular wäre nur
// ehrlich, wenn dahinter dasselbe stünde.
//
// Bedienelemente stehen absichtlich NICHT hier: die Auswahl-Checkbox in
// jobSelection und die Aktions-Spalte in sharedJobs sind keine Datenspalten
// und haben keinen Key — eine Config kann sie deshalb auch nicht wegnehmen.
//
// Das VOKABULAR steht nicht hier, sondern in src/fieldVisibility/fieldCatalog.ts:
// welche Angaben es gibt und was fachlich worin steckt. Diese Datei sagt nur, wie
// eine bestimmte Tabelle sie darstellt — welcher Key dort eine eigene Spalte ist
// (`partOf` fehlt), welcher in einer Zelle mitläuft (`partOf` gesetzt), unter
// welchem Gruppenkopf er steht und in welcher Reihenfolge. Ein Test hält fest,
// dass jeder Key hier im Katalog existiert.
export interface JobTableColumnMeta {
    /** Config-Key der Spalte; identisch mit der Spalten-`id` im ColumnDef. */
    key: string;
    /**
     * i18n-Schlüssel des Labels, unter dem die Spalte in #892 und in der
     * Dokumentation erscheint. Meist ist das auch die Spaltenüberschrift — bei
     * den Sammelspalten aber nicht: dort steht in der Tabelle „Name", während
     * im Config-Editor „Kunde, zusammengefasst" gebraucht wird, weil sonst zwei
     * Zeilen unter „Kunde" gleich hießen.
     */
    labelKey: string;
    /** Gruppenkopf, unter dem die Spalte steht; Spalten ohne Gruppe stehen frei. */
    group?: 'partner' | 'customer' | 'address';
    /**
     * Bestandteil der Angabe mit diesem Key. Fehlt partOf, ist der Eintrag eine
     * eigene Spalte: ausblendbar UND positionierbar. Ein Bestandteil ist nur
     * ausblendbar und trägt keine Position — er hat keinen Platz in der Tabelle,
     * sondern einen in einer Zelle.
     *
     * Genannt wird der DIREKTE Träger. Ketten sind erlaubt und entstehen in
     * sharedJobs: customer.lastname -> customer.name -> customer.summary.
     *
     * partOf ersetzt einen zusätzlichen Marker wie `kind: 'field'`: wer ein
     * partOf hat, IST ein Bestandteil. Eine Wahrheit statt zweier, die jemand
     * gleich halten müsste.
     */
    partOf?: string;
}

// Bedienelemente: keine Datenspalten, keine Config. AUSDRÜCKLICH benannt und
// nicht daraus erschlossen, dass ein Key in der Registry fehlt — sonst gälte
// eine später ergänzte Datenspalte ohne Registry-Eintrag als Bedienelement und
// wäre damit stillschweigend weder ausblendbar noch verschiebbar.
export const CONTROL_COLUMN_IDS: Set<string> = new Set(['select', 'actions']);

// Tabelle A — „Aufträge teilen" (SubAccountShareList) und die Auftragsauswahl im
// Export-Assistenten (ExportWizardDialog). Reihenfolge = Standardreihenfolge.
// 'customer.summary' und 'meter.meterAddress.full' sind die Sammelspalten, die
// heute als Rückfall entstehen, wenn die Automatik alle Bestandteile ausblendet.
export const JOB_SELECTION_COLUMNS: JobTableColumnMeta[] = [
    { key: 'job.isShared', labelKey: 'management.accounts.share.tableColumns.isShared' },
    { key: 'meter.meterType', labelKey: 'meterReadingJob.type' },
    { key: 'job.status', labelKey: 'meterReadingJob.status' },
    { key: 'meter.meterNumber', labelKey: 'meterReadingJob.meter' },
    { key: 'meter.meterAddress.objectKey', labelKey: 'meterReadingJob.objectKey' },
    { key: 'partner.identifier', labelKey: 'meterReadingJob.partner.identifier', group: 'partner' },
    { key: 'partner.name', labelKey: 'meterReadingJob.partner.name', group: 'partner' },
    // Die Partner-Spalte „Name" ist eine Zelle aus drei Feldern; erst diese drei
    // Einträge machen sie einzeln wegnehmbar.
    { key: 'partner.company', labelKey: 'meterReadingJob.partner.company', group: 'partner', partOf: 'partner.name' },
    { key: 'partner.lastname', labelKey: 'meterReadingJob.partner.lastname', group: 'partner', partOf: 'partner.name' },
    { key: 'partner.firstname', labelKey: 'meterReadingJob.partner.firstname', group: 'partner', partOf: 'partner.name' },
    { key: 'customer.customerNumber', labelKey: 'meterReadingJob.customer.customernumber', group: 'customer' },
    { key: 'customer.company', labelKey: 'meterReadingJob.customer.company', group: 'customer' },
    { key: 'customer.name', labelKey: 'meterReadingJob.customer.name', group: 'customer' },
    { key: 'customer.lastname', labelKey: 'meterReadingJob.customer.lastname', group: 'customer', partOf: 'customer.name' },
    { key: 'customer.firstname', labelKey: 'meterReadingJob.customer.firstname', group: 'customer', partOf: 'customer.name' },
    // Eigenes Label, nicht das der Namensspalte: #892 zeigt Labels statt Keys,
    // und zwei Zeilen „Name" unter „Kunde" wären dort nicht unterscheidbar.
    { key: 'customer.summary', labelKey: 'meterReadingJob.customer.summary', group: 'customer' },
    { key: 'meter.meterAddress.street', labelKey: 'meterReadingJob.address.street', group: 'address' },
    // Es gibt bewusst KEINEN Key für „nur den Straßennamen": „Hauptstraße 12"
    // ohne „Hauptstraße" wäre eine Nummer ohne Bezug.
    {
        key: 'meter.meterAddress.houseNumber',
        labelKey: 'meterReadingJob.address.houseNumber',
        group: 'address',
        partOf: 'meter.meterAddress.street',
    },
    { key: 'meter.meterAddress.zip', labelKey: 'meterReadingJob.address.zip', group: 'address' },
    { key: 'meter.meterAddress.city', labelKey: 'meterReadingJob.address.city', group: 'address' },
    { key: 'meter.meterAddress.cityDistrict', labelKey: 'meterReadingJob.address.cityDistrict', group: 'address' },
    { key: 'meter.meterAddress.full', labelKey: 'meterReadingJob.address.fullAddress', group: 'address' },
    { key: 'jobConstraints.latestExecution', labelKey: 'meterReadingJob.jobConstraints.latestExecution' },
];

// Tabelle B — Zugangs-Detail (Auge-Button, SubAccountDetails). Sechs Spalten,
// weil Kunde und Adresse dort bereits zusammengefasste Zellen sind; sie tragen
// dieselben Keys wie die Sammelspalten in Tabelle A. Was in Tabelle A eine
// eigene Spalte ist (Firma, Name, Straße, PLZ, Ort), ist hier ein Bestandteil
// dieser Zellen — derselbe Key, dieselbe Bedeutung, nur ohne eigene Spalte.
// Gruppieren ist in dieser Tabelle ohnehin abgeschaltet (`groupable={false}`).
//
// Der Stadtteil fehlt bewusst: die Adress-Zelle rendert ihn hier nicht.
export const SHARED_JOBS_COLUMNS: JobTableColumnMeta[] = [
    { key: 'meter.meterType', labelKey: 'meterReadingJob.type' },
    { key: 'job.status', labelKey: 'meterReadingJob.status' },
    { key: 'meter.meterNumber', labelKey: 'meterReadingJob.meterNumber' },
    { key: 'meter.meterAddress.objectKey', labelKey: 'meterReadingJob.objectKey' },
    { key: 'customer.summary', labelKey: 'meterReadingJob.customer.title' },
    { key: 'customer.company', labelKey: 'meterReadingJob.customer.company', partOf: 'customer.summary' },
    { key: 'customer.name', labelKey: 'meterReadingJob.customer.name', partOf: 'customer.summary' },
    { key: 'customer.lastname', labelKey: 'meterReadingJob.customer.lastname', partOf: 'customer.name' },
    { key: 'customer.firstname', labelKey: 'meterReadingJob.customer.firstname', partOf: 'customer.name' },
    { key: 'meter.meterAddress.full', labelKey: 'meterReadingJob.address.title' },
    { key: 'meter.meterAddress.street', labelKey: 'meterReadingJob.address.street', partOf: 'meter.meterAddress.full' },
    {
        key: 'meter.meterAddress.houseNumber',
        labelKey: 'meterReadingJob.address.houseNumber',
        partOf: 'meter.meterAddress.street',
    },
    { key: 'meter.meterAddress.zip', labelKey: 'meterReadingJob.address.zip', partOf: 'meter.meterAddress.full' },
    { key: 'meter.meterAddress.city', labelKey: 'meterReadingJob.address.city', partOf: 'meter.meterAddress.full' },
];

export const JOB_TABLE_COLUMNS: Record<JobTableName, JobTableColumnMeta[]> = {
    jobSelection: JOB_SELECTION_COLUMNS,
    sharedJobs: SHARED_JOBS_COLUMNS,
};
